import { DbEnum } from "../../../generated/prisma-enums";

type VibeDeploymentStatusLiteral = (typeof DbEnum.VIBE_DEPLOYMENT_STATUS_VALUES)[number];

/**
 * What a deploy ATTEMPT came to — the three-way reading of
 * `VibeDeploymentStatus` that a history strip and a success rate both need.
 *
 * Nine statuses answer "where is this row in the state machine". A card asks a
 * different question — "did this push ship?" — and the answer is three-valued,
 * not eight-valued and not two-valued. It is not two-valued because a row that
 * is still BUILDING has no outcome at all, and folding it into either side
 * makes a success rate move every time a deploy starts.
 *
 * 🔴 **THIS IS A CLASSIFICATION OF THE TERMINAL STATUS, NOT OF THE HISTORY THE
 * ROW LIVED THROUGH — AND THE DIFFERENCE IS NOT RECOVERABLE FROM THE DATABASE.**
 * `VibeDeployment` carries no "went healthy at" stamp: the only witness that a
 * row ever occupied the live slot is its CURRENT status. Two transitions
 * destroy that witness — `HEALTHY -> ROLLED_BACK` (post-flip health check
 * failed) and `HEALTHY -> FAILED` (`markPulledFromService`, the crash-loop
 * pull) — so a deployment that served real traffic and then broke is
 * indistinguishable here from one whose build never compiled. Both count
 * `FAILED`.
 *
 * That is the honest reading and it is deliberately the pessimistic one: a
 * deploy that had to be pulled did not ship, whatever it did in between. **Do
 * not label any figure derived from this "attempts that reached HEALTHY".** No
 * column answers that question, and the audit trail cannot either —
 * `VibeAuditEvent` carries no `vibeDeploymentId`, its `DEPLOYMENT_HEALTHY` row
 * is emitted more than once for a restored deployment, and the admin
 * escape-hatch emits none at all.
 *
 * The switch is exhaustive, so a ninth `VibeDeploymentStatus` is a compile
 * error here rather than a silent fall into one bucket. That is the whole
 * reason this is a function in `@nexus/types` and not a predicate written twice
 * — the server counts with it and the console colours its ticks with it, so a
 * tick can never render green over a status the rate beside it counts as
 * failed.
 */
export const VIBE_DEPLOYMENT_OUTCOME_VALUES = [
  /// The attempt is serving, or was cleanly handed over to a successor that
  /// went healthy. `HEALTHY` and `SUPERSEDED` — and `SUPERSEDED` is provable
  /// rather than assumed: its only source in the state machine is `HEALTHY`.
  "SUCCEEDED",
  /// The attempt is terminal and is not serving. Read the caveat above before
  /// describing this as "the build failed" — it also holds a deployment that
  /// served and was later rolled back or pulled.
  "FAILED",
  /// No outcome yet. The row is still moving: building, waiting on a human, or
  /// mid-cutover. Counted separately so a running deploy never moves a rate.
  "IN_FLIGHT"
] as const;

export type VibeDeploymentOutcome = (typeof VIBE_DEPLOYMENT_OUTCOME_VALUES)[number];

/**
 * Classify one deploy attempt by its current status. Pure, total, exhaustive.
 */
export function vibeDeploymentOutcome(status: VibeDeploymentStatusLiteral): VibeDeploymentOutcome {
  switch (status) {
    case "HEALTHY":
    case "SUPERSEDED":
      return "SUCCEEDED";
    // CANCELLED (stopped on request) did not ship: the same pessimistic reading
    // as DISPLACED — terminal and not serving — for the same reason.
    case "FAILED":
    case "ROLLED_BACK":
    case "DISPLACED":
    case "CANCELLED":
      return "FAILED";
    case "BUILDING":
    case "AWAITING_APPROVAL":
    case "DEPLOYING":
      return "IN_FLIGHT";
    default: {
      const exhaustive: never = status;
      void exhaustive;
      // Unreachable while the switch is exhaustive. IN_FLIGHT rather than
      // FAILED is the safe fall: an unclassified status must not make a rate
      // look worse than it is, and it keeps `succeeded + failed` a set whose
      // every member was deliberately placed.
      return "IN_FLIGHT";
    }
  }
}
